import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  withHandlers,
  lifecycle
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Grid from '@material-ui/core/Grid';
import CardMedia from '@material-ui/core/CardMedia';
import { getAccounts, addShopAccount } from '../../actions/Accounts';
import BackButton from '../../components/atoms/BackButton';
import RegisterButton from '../../components/atoms/RegisterButton';
import ImagePreview from '../../components/molecules/ImagePreview';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import { getShops, addShopImage } from '../../actions/Shop';
import { resizeImage, uploadFile, sendLogin } from '../../auth/Auth';

const styles = {
  root: {
    position: 'absolute',
    width: '85%',
    height: 'auto',
    maxHeight: '80%',
    top: '5%',
    left: '2%',
    backgroundColor: '#fff',
    padding: 28,
    overflow: 'scroll',
    outline: 'none'
  },
  mainPaper: {
    position: 'relative',
    margin: '1px 0px 10px',
    padding: '10px 26px 10px 26px'
  },
  pageTitle: {
    margin: '20px 0px 0px',
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  description: {
    color: '#707070',
    margin: '20px 0px 0px'
  },
  subTitle: {
    margin: '20px 0px 0px',
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  divider: {
    margin: '20px 0px'
  },
  detailTable: {
    width: 'auto'
  },
  tableCellItem: {
    border: '2px solid #ffffff',
    backgroundColor: '#e0e0e0'
  },
  tableCellData: {
    border: '2px solid #ffffff'
  },
  textField100: {
    padding: '0px 5px 0px 5px',
    width: '100px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px'
  },
  textFieldLong: {
    padding: '0px 5px 0px 5px',
    width: '500px'
  },
  editTable: {
    width: 'auto'
  },
  tableCell: {
    border: '2px solid #ffffff'
  },
  marginBottom12: {
    marginBottom: '12px'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account,
      prefectures: state.commons.prefectures,
      shopCategories: state.commons.shopCategories
    }),
    dispatch => ({
      actions: bindActionCreators(
        { getAccounts, getShops, addShopAccount, addShopImage },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openShopAddProgress', 'updateOpenShopAddProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isShopAddSuccess', 'updateIsShopAddSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('prefecture', 'setPrefecture', 0),
  withState('shopCategory', 'setShopCategory', 0),
  withState('phone', 'setPhone', ''),
  withState('phoneAuthCode', 'setPhoneAuthCode', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleRedirect: props => path => {
      props.history.push(path);
    }
  }),
  lifecycle({
    componentDidMount() {
      this.props.actions.getAccounts({ cognitoUserId: '' });
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    account,
    checkSession,
    openShopAddProgress,
    updateOpenShopAddProgress,
    loading,
    updateLoading,
    isShopAddSuccess,
    updateIsShopAddSuccess,
    resultMessage,
    setResultMessage,
    handleRedirect,
    searchKey,
    groupShop,
    enableShopBank,
    backAction,
    addConfirm,
    addParam,
    prefectures,
    shopCategories,
    updateOpenShopAdd,
    updateOpenShopAddConfirm
  }) => {
    const isGroup = typeof account !== 'undefined' && account.role === 'GROUP';
    const isSecretariat =
      typeof account !== 'undefined' && account.role === 'SECRETARIAT';

    const registerAction = async () => {
      updateLoading(true);
      updateOpenShopAddProgress(true);
      const addedShop = await actions
        .addShopAccount(addParam)
        .then(data => checkSession(data));
      const imagePaths = await Promise.all(
        Object.keys(addParam.images).map(async i => {
          if (addParam.images[i].image !== null) {
            let { image } = addParam.images[i];
            if (image.size > Number(process.env.REACT_APP_RESIZE_BORDER)) {
              const { resizeImageFile } = await resizeImage(image);
              image = resizeImageFile;
            }
            const imagePath = await uploadFile(
              'shop',
              addedShop.payload.shop.shopId,
              image,
              'public'
            );
            return imagePath;
          }
          return null;
        })
      );
      const images = imagePaths
        .filter(i => i !== null)
        .map((i, index) => {
          return { image: i, viewOrder: index };
        });
      if (Object.keys(images).length !== 0) {
        await actions
          .addShopImage(addedShop.payload.shop.shopId, {
            images
          })
          .then(data => checkSession(data));
      }
      if (addedShop.type === 'ADD_SHOP_ACCOUNT_SUCCESS') {
        updateIsShopAddSuccess(true);
        if (!groupShop) {
          actions.getShops(searchKey).then(data => checkSession(data));
        }
        setResultMessage('登録が完了しました。');
        updateLoading(false);
      } else {
        updateIsShopAddSuccess(false);
        setResultMessage('登録に失敗しました。');
        updateLoading(false);
      }
    };

    const progressOkAction = () => {
      if (isShopAddSuccess) {
        updateOpenShopAdd(false);
        updateOpenShopAddConfirm(false);
        updateOpenShopAddProgress(false);
        if (groupShop && isSecretariat) {
          handleRedirect(`/groups?groupId=${searchKey.groupId}`);
        } else if (groupShop && isGroup) {
          handleRedirect('/groups');
        }
      } else {
        updateOpenShopAddProgress(false);
      }
    };

    return (
      <div className={classes.root}>
        <Typography variant="h6">店舗登録申込書</Typography>
        <Paper className={classes.mainPaper} elevation={1}>
          <Table className={classes.detailTable}>
            <TableRow>
              <TableCell className={classes.tableCellItem}>店舗名</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.shopName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                店舗名かな
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.shopNameKana}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                店舗電話番号
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.phone}
                {'('}
                {addConfirm.showPhone ? `表示` : `非表示`}
                {')'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                こども食堂設定
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.childShopFlag ? `設定済` : ``}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>郵便番号</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.zipCode}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>都道府県</TableCell>
              <TableCell className={classes.tableCellData}>
                {prefectures.map(item => {
                  if (
                    addConfirm.prefectureCode.toString() === item.id.toString()
                  ) {
                    return item.name;
                  }
                  return '';
                })}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>市区町村</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.city}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                町域・番地
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.address1}
              </TableCell>
            </TableRow>
            {/* <TableRow>
              <TableCell className={classes.tableCellItem}>番地</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.address2}
              </TableCell>
            </TableRow> */}
            <TableRow>
              <TableCell className={classes.tableCellItem}>建物名</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.building}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                店舗カテゴリー
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {shopCategories.map(item => {
                  if (
                    addConfirm.shopCategoryId &&
                    addConfirm.shopCategoryId.toString() === item.id.toString()
                  ) {
                    return item.name;
                  }
                  return '';
                })}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                運営会社名
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.shopGroupName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                ご担当者名
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.shopContactName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>所属部署</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.department}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                電話番号（担当者）
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.contactPhone}
              </TableCell>
            </TableRow>
            {enableShopBank && (
              <>
                <TableRow>
                  <TableCell className={classes.tableCellItem}>
                    インボイス制度 適格事業者登録
                  </TableCell>
                  <TableCell className={classes.tableCellData}>
                    {addConfirm.registrationId ? '登録済み' : '未登録'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.tableCellItem}>
                    適格事業者登録番号
                  </TableCell>
                  <TableCell className={classes.tableCellData}>
                    {addConfirm.registrationId}
                  </TableCell>
                </TableRow>
              </>
            )}
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                メールアドレス
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.email}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>店舗概要</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.introduction}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>営業時間</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.businessHours}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>定休日</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.holiday}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>価格帯</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.priceRange}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>最寄駅</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.nearestStation}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>アクセス</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.access}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                WebページURL
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.webPage}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>タグ</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.tagStr}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>画像</TableCell>
              <TableCell className={classes.tableCellData}>
                <ImagePreview previewImage={addConfirm.previewImage1} />
                <ImagePreview previewImage={addConfirm.previewImage2} />
                <ImagePreview previewImage={addConfirm.previewImage3} />
                <ImagePreview previewImage={addConfirm.previewImage4} />
                <ImagePreview previewImage={addConfirm.previewImage5} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>SNS</TableCell>
              <TableCell className={classes.tableCellData}>
                <div className={classes.margin}>
                  <Grid container spacing={1} alignItems="flex-end">
                    <Grid item>
                      <CardMedia
                        component="img"
                        alt="Facebook"
                        height="24"
                        image="/images/icon_facebook.png"
                        title="Facebook"
                        className={classes.marginBottom12}
                      />
                    </Grid>
                    <Grid item xs={12} sm container>
                      <TableCell className={classes.tableCellData}>
                        {addConfirm.snsFacebook}
                      </TableCell>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} alignItems="flex-end">
                    <Grid item>
                      <CardMedia
                        component="img"
                        alt="Twitter"
                        height="24"
                        image="/images/icon_twitter.png"
                        title="Twitter"
                        className={classes.marginBottom12}
                      />
                    </Grid>
                    <Grid item xs={12} sm container>
                      <TableCell className={classes.tableCellData}>
                        {addConfirm.snsTwitter}
                      </TableCell>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} alignItems="flex-end">
                    <Grid item>
                      <CardMedia
                        component="img"
                        alt="Instagram"
                        height="24"
                        image="/images/icon_instagram.png"
                        title="Instagram"
                        className={classes.marginBottom12}
                      />
                    </Grid>
                    <Grid item xs={12} sm container>
                      <TableCell className={classes.tableCellData}>
                        {addConfirm.snsInstagram}
                      </TableCell>
                    </Grid>
                  </Grid>
                </div>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>座席</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.numberSit}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>禁煙/喫煙</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.smoking}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>駐車場</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.parking}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                店舗詳細表示制限
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.stealthModeStr}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                誰でもごち制限
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.gochiRestrictionStr}
              </TableCell>
            </TableRow>
            {/* <TableRow>
              <TableCell className={classes.tableCellItem}>
                社食ごちめし現金授受の有無
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.cashAllowed ? '受け付ける' : '受け付けない'}
              </TableCell>
            </TableRow> */}
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                社食ごちめし金額指定チケット利用の有無
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.allowMenuPriceShashoku ? '利用する' : '利用しない'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                ごちめしの紹介者
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.preGochiUserStr}
              </TableCell>
            </TableRow>
            {enableShopBank && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  入金管理
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.depositManageStr}
                </TableCell>
              </TableRow>
            )}
            {enableShopBank && addParam.depositManage === 'NONSELF' && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>銀行名</TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankName}
                </TableCell>
              </TableRow>
            )}
            {enableShopBank && addParam.depositManage === 'NONSELF' && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>支店名</TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankBranchName}
                </TableCell>
              </TableRow>
            )}
            {enableShopBank && addParam.depositManage === 'NONSELF' && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座種別
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankAccountTypeStr}
                </TableCell>
              </TableRow>
            )}
            {enableShopBank && addParam.depositManage === 'NONSELF' && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座番号
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankAccountNumber}
                </TableCell>
              </TableRow>
            )}
            {enableShopBank && addParam.depositManage === 'NONSELF' && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座名義
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankAccountHolder}
                </TableCell>
              </TableRow>
            )}
          </Table>
        </Paper>
        <div>
          <BackButton onClickAction={backAction} />
          <RegisterButton onClickAction={registerAction} />
        </div>
        <ProgressDialog
          openProgress={openShopAddProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
